import React, { useState, useEffect, useRef } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { Alert, AlertTitle, Color } from '@material-ui/lab';
import { DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS } from '../../utils/Constants/AlertStatusConstants';
import { useTranslation } from 'react-i18next';

interface AutoDismissAlertProps {
  message: string;
  title?: string;
  duration?: number;
  severity?: Color | string;
  onClose?: () => void;
  variant?: "filled" | "outlined" | "standard";
  closable?: boolean;
  customButtonText?: string;
  onCustomButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const useStyles = makeStyles(() => (
  {
    '@keyframes bounceIn': {
      '0%': {
        opacity: 0,
        transform: 'scale(0.3)',
      },
      '50%': {
        opacity: 1,
        transform: 'scale(1.05)',
      },
      '70%': {
        transform: 'scale(0.9)',
      },
      '100%': {
        opacity: 1,
        transform: 'scale(1)',
      },
    },
    '@keyframes fadeOut': {
      '0%': {
        opacity: 1,
        transform: 'scale(1)',
      },
      '100%': {
        opacity: 0,
        transform: 'scale(0.5)',
      },
    },
    alertContainer: {
      width: 'auto',
      marginBottom: 16,
      position: 'fixed',
      top: '100px',
      zIndex: 100,
      right: '30px',
      animation: '$bounceIn 0.5s ease-in forwards !important',
    },
    alertExiting: {
      animation: '$fadeOut 0.3s ease-in forwards !important',
    },
    customButton: {
      marginLeft: 16,
      padding: '4px 12px',
      border: 'none',
      borderRadius: 4,
      background: 'transparent',
      color: '#236BFF',
      fontWeight: 700,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontSize: 13,
      fontFamily: "Rubik, -apple-system, BlinkMacSystemFont, \"Source Sans Pro\", \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Cantarell\", \"Fira Sans\", \"Droid Sans\", \"Helvetica Neue\", 'sans-serif'",
    },
    messageWithButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontFamily: "Rubik, -apple-system, BlinkMacSystemFont, \"Source Sans Pro\", \"Segoe UI\", \"Roboto\", \"Oxygen\", \"Ubuntu\", \"Cantarell\", \"Fira Sans\", \"Droid Sans\", \"Helvetica Neue\", 'sans-serif'",
    },
  }
));

const AutoDismissAlert = ({
  message,
  title,
  duration = DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS,
  severity = "success",
  onClose,
  variant = "standard",
  closable = true,
  customButtonText,
  onCustomButtonClick
}: AutoDismissAlertProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [mounted, setMounted] = useState(true);
  const [exiting, setExiting] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const exitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const remainingTimeRef = useRef(duration);
  const pauseStartRef = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);
    setExiting(false);
    setAnimationKey(prev => prev + 1);

    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => {
      startExitAnimation();
    }, duration);

    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, duration]);

  const handleMouseEnter = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
      pauseStartRef.current = Date.now();
    }
  };

  const handleMouseLeave = () => {
    if (pauseStartRef.current) {
      remainingTimeRef.current = Math.max(0, DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS);
      pauseStartRef.current = null;
      if (remainingTimeRef.current > 0) {
        exitTimerRef.current = setTimeout(() => {
          startExitAnimation();
        }, remainingTimeRef.current);
      } else {
        startExitAnimation();
      }
    }
  };

  const startExitAnimation = () => {
    setExiting(true);
    setTimeout(() => {
      setMounted(false);
      if (onClose) onClose();
    }, 300);
  };

  const handleClose = () => {
    startExitAnimation();
  };

  if (!mounted) return null;

  return (
    <Box
      key={animationKey}
      className={`${classes.alertContainer} ${exiting ? classes.alertExiting : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Alert
        severity={severity as Color}
        {...(closable ? { onClose: handleClose } : {})}
        variant={variant as "filled" | "outlined" | "standard"}
        style={{ borderRadius: 8 }}
      >
        {title && <AlertTitle>{t(title)}</AlertTitle>}
        <span className={customButtonText ? classes.messageWithButton : undefined}>
          {t(message)}
          {customButtonText && (
            <button
              className={classes.customButton}
              onClick={onCustomButtonClick}
              type="button"
            >
              {customButtonText}
            </button>
          )}
        </span>
      </Alert>
    </Box >
  );
};

export default AutoDismissAlert;